import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useTranslation } from '../../i18n';
import { colors, radius } from '../../theme/tokens';

/**
 * Modal "Como você deseja pagar?".
 *
 * Substitui o `#paymentModal` de `planos_lista.blade.php`. As três opções e a
 * ordem (PIX, cartão, boleto) são as mesmas — PIX primeiro porque é o caminho
 * de confirmação imediata e o mais importante do produto.
 */

export type PaymentChoice = 'PIX' | 'CARTAO' | 'BOLETO';

interface PaymentMethodDialogProps {
  open: boolean;
  onClose: () => void;
  onChoose: (method: PaymentChoice) => void;
}

const TITLE_ID = 'payment-method-dialog-title';

interface MethodOption {
  method: PaymentChoice;
  icon: React.ReactNode;
  name: string;
  hint: string;
  badge?: string;
  /** Cor de destaque no hover — sempre um token, nunca hex de marca. */
  accent: string;
}

export function PaymentMethodDialog({ open, onClose, onChoose }: PaymentMethodDialogProps) {
  const { t } = useTranslation();

  const options: MethodOption[] = [
    {
      method: 'PIX',
      icon: <QrCode2Icon sx={{ fontSize: 28, color: colors.success }} />,
      name: t.payment.pix.name,
      hint: t.payment.pix.hint,
      badge: t.payment.pix.badge,
      accent: colors.success,
    },
    {
      method: 'CARTAO',
      icon: <CreditCardIcon sx={{ fontSize: 28, color: colors.primary }} />,
      name: t.payment.card.name,
      hint: t.payment.card.hint,
      // O cartão herda a cor da marca: é o único método que continua dentro do site.
      accent: colors.primary,
    },
    {
      method: 'BOLETO',
      icon: <ReceiptLongIcon sx={{ fontSize: 28, color: colors.warning }} />,
      name: t.payment.boleto.name,
      hint: t.payment.boleto.hint,
      accent: colors.warning,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: radius.lg } } }}
    >
      <DialogTitle id={TITLE_ID} sx={{ fontSize: '1.125rem', fontWeight: 600, pb: 1 }}>
        {t.payment.chooseTitle}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          {options.map((option) => (
            <Button
              key={option.method}
              type="button"
              fullWidth
              onClick={() => onChoose(option.method)}
              sx={{
                justifyContent: 'space-between',
                textAlign: 'left',
                textTransform: 'none',
                p: 2,
                border: `1px solid ${colors.border}`,
                borderRadius: radius.sm,
                color: colors.textPrimary,
                transition: 'border-color 200ms ease, background-color 200ms ease',
                '&:hover': {
                  borderColor: option.accent,
                  backgroundColor: colors.surfaceMuted,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                {option.icon}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, color: colors.textPrimary }}>
                    {option.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
                    {option.hint}
                  </Typography>
                </Box>
              </Box>

              {option.badge ? (
                <Typography
                  component="span"
                  sx={{ flexShrink: 0, ml: 1, fontWeight: 700, color: option.accent }}
                >
                  {option.badge}
                </Typography>
              ) : null}
            </Button>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: colors.surfaceMuted, px: 3, py: 2 }}>
        <Button
          type="button"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: colors.textSecondary,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.sm,
            px: 2,
            '&:hover': { backgroundColor: colors.surface },
          }}
        >
          {t.common.cancel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
